@file:OptIn(kotlin.concurrent.atomics.ExperimentalAtomicApi::class)

package com.dotpx.ambient

import kotlin.concurrent.atomics.AtomicLong
import kotlin.concurrent.atomics.AtomicReference
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.hypot
import kotlin.math.max
import kotlin.math.sin

private class MorphCommand(
    val kind: Int,
    val scene: AmbienceScene?,
    val seed: ULong,
    val durationFrames: Long,
    val generation: Long,
)

/**
 * Owns two fixed renderers and performs an equal-power A/B transition.
 * UI commands and audio-thread snapshots cross through atomic references only.
 */
class MorphingAmbienceRenderer internal constructor(
    val sampleRate: Double,
    seed: ULong,
    scene: AmbienceScene,
    usePresetCombination: Boolean = false,
    private val materialCycle: SoundMaterialCycle? = null,
) : PcmRenderer {
    private val stateA = LayeredAmbienceRenderer(
        sampleRate,
        seed,
        scene,
        usePresetCombination,
        materialCycle,
    )
    private val stateB = LayeredAmbienceRenderer(
        sampleRate,
        seed xor 0xA076_1D64_78BD_642FuL,
        scene,
    )

    private val commandReference = AtomicReference<MorphCommand?>(null)
    private val generationReference = AtomicLong(0L)
    private val statusReference = AtomicReference(
        PackedMorphStatus(
            activeScene = scene,
            activeSeed = seed,
            activeMatchesPresetCombination = stateA.matchesPresetCombination,
        ),
    )
    private val visualReference = AtomicReference(PackedVisualTelemetry())

    private var activeIndex = 0
    private var outgoingIndex = 0
    private var incomingIndex = 1
    private var renderMode = 0 // 0 stable, 1 normal morph, 2 fast settle
    private var currentCommandGeneration = 0L
    private var baseTransitionGeneration = 0L
    private var baseTransitionProgress = 1f
    private var consumedCommandGeneration = 0L
    private var completedCommandGeneration = 0L
    private var renderedFrames = 0L
    private var totalFrames = 2L
    private var outgoingGain = 1.0
    private var incomingGain = 0.0
    private var rotationCos = 1.0
    private var rotationSin = 0.0
    private var settleTargetIndex = 0
    private var sceneA = scene
    private var sceneB = scene
    private var seedA = seed
    private var seedB = seed xor 0xA076_1D64_78BD_642FuL
    private var targetScene: AmbienceScene? = null
    private var targetSeed = 0uL
    private var outputLeft = 0f
    private var outputRight = 0f

    val status: AmbienceMorphStatus
        get() = statusReference.load().snapshot()

    val visualFrame: AmbienceVisualFrame
        get() = visualReference.load().snapshot()

    internal val debugActiveTexture: TextureKind
        get() = if (activeIndex == 0) stateA.debugTexture else stateB.debugTexture
    internal val debugActiveEvent: EventKind
        get() = if (activeIndex == 0) stateA.debugEvent else stateB.debugEvent
    internal val debugActiveSecondaryEvent: EventKind
        get() = if (activeIndex == 0) stateA.debugSecondaryEvent else stateB.debugSecondaryEvent

    init {
        publishVisual()
    }

    /** Returns null while the one-entry UI-to-audio mailbox is occupied. */
    fun requestMorph(scene: AmbienceScene, seed: ULong, durationFrames: Long): Long? {
        val generation = nextGeneration()
        val command = MorphCommand(
            kind = COMMAND_MORPH,
            scene = scene,
            seed = seed,
            durationFrames = max(durationFrames, 2L),
            generation = generation,
        )
        return if (commandReference.compareAndSet(null, command)) generation else null
    }

    /** Settles an in-flight blend at the currently louder endpoint. */
    fun requestSettle(durationFrames: Long): Long? {
        val generation = nextGeneration()
        val command = MorphCommand(
            kind = COMMAND_SETTLE,
            scene = null,
            seed = 0uL,
            durationFrames = max(durationFrames, 2L),
            generation = generation,
        )
        return if (commandReference.compareAndSet(null, command)) generation else null
    }

    override fun render(interleaved: FloatArray, frameCount: Int) {
        require(frameCount >= 0 && interleaved.size >= frameCount * 2) {
            "interleaved buffer must contain two floats per frame"
        }
        consumePendingCommand()
        var offset = 0
        repeat(frameCount) {
            nextStereoSample()
            interleaved[offset] = outputLeft
            interleaved[offset + 1] = outputRight
            offset += 2
        }
        publishStatus()
        publishVisual()
    }

    private fun nextGeneration(): Long {
        while (true) {
            val current = generationReference.load()
            val next = if (current == Long.MAX_VALUE) 1L else current + 1L
            if (generationReference.compareAndSet(current, next)) return next
        }
    }

    private fun consumePendingCommand() {
        val command = commandReference.load() ?: return
        if (!commandReference.compareAndSet(command, null)) return
        when {
            command.kind == COMMAND_MORPH && renderMode == 0 && command.scene != null -> {
                beginMorph(command.scene, command.seed, command.durationFrames, command.generation)
            }
            command.kind == COMMAND_SETTLE && renderMode != 0 -> {
                beginSettle(command.durationFrames, command.generation)
            }
            else -> completedCommandGeneration = command.generation
        }
        consumedCommandGeneration = command.generation
    }

    private fun beginMorph(
        scene: AmbienceScene,
        seed: ULong,
        durationFrames: Long,
        generation: Long,
    ) {
        outgoingIndex = activeIndex
        incomingIndex = if (activeIndex == 0) 1 else 0
        if (incomingIndex == 0) {
            stateA.reset(seed, scene, materialCycle = materialCycle)
            sceneA = scene
            seedA = seed
        } else {
            stateB.reset(seed, scene, materialCycle = materialCycle)
            sceneB = scene
            seedB = seed
        }
        renderMode = 1
        currentCommandGeneration = generation
        baseTransitionGeneration = generation
        baseTransitionProgress = 0f
        renderedFrames = 0L
        totalFrames = max(durationFrames, 2L)
        outgoingGain = 1.0
        incomingGain = 0.0
        targetScene = scene
        targetSeed = seed
        configureRotation(PI / 2.0, totalFrames)
    }

    private fun beginSettle(durationFrames: Long, generation: Long) {
        // Preserve the morph generation and its last progress during the short
        // settle. The visual field follows active base signals without a new burst.
        if (renderMode == 1) baseTransitionProgress = (renderedFrames.toDouble() / totalFrames).toFloat()
        val currentAngle = atan2(incomingGain, outgoingGain)
        val targetAngle: Double
        if (incomingGain >= outgoingGain) {
            targetAngle = PI / 2.0
            settleTargetIndex = incomingIndex
        } else {
            targetAngle = 0.0
            settleTargetIndex = outgoingIndex
        }
        renderMode = 2
        currentCommandGeneration = generation
        renderedFrames = 0L
        totalFrames = max(durationFrames, 2L)
        configureRotation(targetAngle - currentAngle, totalFrames)
    }

    private fun configureRotation(angleDelta: Double, frames: Long) {
        val step = angleDelta / max(frames - 1L, 1L).toDouble()
        rotationCos = cos(step)
        rotationSin = sin(step)
    }

    private fun nextStereoSample() {
        if (renderMode == 0) {
            sample(activeIndex)
            outputLeft = softLimit(if (activeIndex == 0) stateA.sampleLeft else stateB.sampleLeft)
            outputRight = softLimit(if (activeIndex == 0) stateA.sampleRight else stateB.sampleRight)
            return
        }

        sample(outgoingIndex)
        val outgoingLeft = if (outgoingIndex == 0) stateA.sampleLeft else stateB.sampleLeft
        val outgoingRight = if (outgoingIndex == 0) stateA.sampleRight else stateB.sampleRight
        sample(incomingIndex)
        val incomingLeft = if (incomingIndex == 0) stateA.sampleLeft else stateB.sampleLeft
        val incomingRight = if (incomingIndex == 0) stateA.sampleRight else stateB.sampleRight
        outputLeft = softLimit(outgoingLeft * outgoingGain.toFloat() + incomingLeft * incomingGain.toFloat())
        outputRight = softLimit(outgoingRight * outgoingGain.toFloat() + incomingRight * incomingGain.toFloat())

        renderedFrames += 1L
        if (renderedFrames >= totalFrames) {
            finishTransition()
        } else {
            val nextIncoming = incomingGain * rotationCos + outgoingGain * rotationSin
            val nextOutgoing = outgoingGain * rotationCos - incomingGain * rotationSin
            incomingGain = nextIncoming
            outgoingGain = nextOutgoing
            if (renderedFrames and 0x3FFL == 0L) {
                val magnitude = hypot(outgoingGain, incomingGain)
                if (magnitude > 0.0) {
                    outgoingGain /= magnitude
                    incomingGain /= magnitude
                }
            }
        }
    }

    private fun finishTransition() {
        if (renderMode == 1) baseTransitionProgress = 1f
        activeIndex = if (renderMode == 1) incomingIndex else settleTargetIndex
        renderMode = 0
        outgoingGain = 1.0
        incomingGain = 0.0
        targetScene = null
        targetSeed = 0uL
        completedCommandGeneration = currentCommandGeneration
    }

    private fun sample(index: Int) {
        if (index == 0) stateA.nextRawStereoSample() else stateB.nextRawStereoSample()
    }

    private fun publishStatus() {
        val remaining = if (renderMode == 0) 0L else max(totalFrames - renderedFrames, 0L)
        statusReference.load().publish(
            isMorphing = renderMode != 0,
            activeScene = if (activeIndex == 0) sceneA else sceneB,
            activeSeed = if (activeIndex == 0) seedA else seedB,
            activeMatchesPresetCombination = if (activeIndex == 0) {
                stateA.matchesPresetCombination
            } else {
                stateB.matchesPresetCombination
            },
            targetScene = targetScene,
            targetSeed = targetSeed,
            remainingFrames = remaining,
            consumedCommandGeneration = consumedCommandGeneration,
            completedCommandGeneration = completedCommandGeneration,
        )
    }

    private fun publishVisual() {
        val weightA: Float
        val weightB: Float
        if (renderMode == 0) {
            weightA = if (activeIndex == 0) 1f else 0f
            weightB = if (activeIndex == 1) 1f else 0f
        } else {
            val outgoingPower = (outgoingGain * outgoingGain).toFloat()
            val incomingPower = (incomingGain * incomingGain).toFloat()
            if (outgoingIndex == 0) {
                weightA = outgoingPower
                weightB = incomingPower
            } else {
                weightA = incomingPower
                weightB = outgoingPower
            }
        }
        val telemetry = visualReference.load()
        telemetry.beginPublish()
        stateA.storeVisualSignals(telemetry, 0, weightA)
        stateB.storeVisualSignals(telemetry, 5, weightB)
        val destinationIndex = when (renderMode) {
            1 -> incomingIndex
            2 -> settleTargetIndex
            else -> activeIndex
        }
        val destination = if (destinationIndex == 0) stateA else stateB
        destination.storeVisualSignals(telemetry, PackedVisualTelemetry.SIGNAL_COUNT, 1f)
        if (renderMode == 1) baseTransitionProgress = (renderedFrames.toDouble() / totalFrames).toFloat()
        telemetry.storeTransition(baseTransitionGeneration, baseTransitionProgress)
        telemetry.endPublish()
    }

    private fun softLimit(value: Float): Float = value / (1f + abs(value))

    private companion object {
        const val COMMAND_MORPH = 1
        const val COMMAND_SETTLE = 2
    }
}
